import loggerInstance from '../../libs/logger/Logger';
import { User } from '../../types';
import { LoginService } from '../LoginService';
import { AccessToken, AccessTokenContext, Permission } from '../types';
import decodeAccessToken from './decodeAccessToken';

class TokenService {
  tokens = new Map<string, AccessToken>();
  promises = new Map<string, Promise<AccessToken>>();

  async getToken(context: AccessTokenContext, permissions?: Permission[]): Promise<AccessToken> {
    const token = this.findTokenForContext(context, permissions);

    if (token) {
      return token;
    }

    return await this.updateToken(context, permissions);
  }

  findTokenForContext(context: AccessTokenContext, permissions?: Permission[]): AccessToken | undefined {
    const contextKey = this.getContextKey(context, permissions);

    return this.tokens.get(contextKey);
  }

  async updateToken(context: AccessTokenContext, permissions?: Permission[]): Promise<AccessToken> {
    loggerInstance.info('Update token started');

    const contextKey = this.getContextKey(context);

    if (this.promises.has(contextKey)) {
      loggerInstance.info('Return token promise (token is already requesting)');

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return this.promises.get(contextKey)!;
    }

    const loginService = new LoginService(LoginServiceBaseUrl);

    loggerInstance.info('Request new token from Login Service');
    const promise = loginService.me(context, permissions).then((token) => {
      const accessToken = { context: context, token, payload: decodeAccessToken(token).payload };

      this.tokens.set(contextKey, accessToken);
      this.promises.delete(contextKey);
      loggerInstance.info('New token received from Login Service');

      return accessToken;
    });

    this.promises.set(contextKey, promise);

    return promise;
  }

  /**Provides the latest received token.
   * The latest because it is the most recent and most likely has not expired yet
   */
  getAny(): AccessToken | undefined {
    const tokens = [...this.tokens.values()];

    if (tokens.length === 0) {
      return;
    }

    return tokens[tokens.length - 1];
  }

  getUser(): User | undefined {
    return this.getAny()?.payload.user;
  }

  private getContextKey(context: AccessTokenContext, permissions?: Permission[]): string {
    if (context === null) {
      return '';
    }

    return `${context.site}-${context.customer}${permissions?.length && permissions.join('-')}`;
  }
}

export default new TokenService();
